<template>
  <tr v-if="isEditMode">
    <td>
      <v-checkbox v-model="bindToCase"></v-checkbox>
    </td>
    <td><div class="mb-2">{{item.title}}</div></td>
    <td>{{item.code}}</td>
    <td>
      <v-text-field
        ref="price"
        v-model="price"
        :rules="priceRules"
        required
        class=""
        :append-icon="priceAdditionalClasses"
        :append-icon-cb="handleResetPriceButtonClick"
      ></v-text-field>
      <confirm ref="confirm"></confirm>
    </td>
    <td>
      <requested-service-quantity
        :caseId="item.caseId"
        :service-id="item.serviceId"
        :requested-quantity="item.quantity"
        :editable="false"
        @requestedQuantityChanged="handleRequestedQuantityChanged"/>
    </td>
    <td>
      <executed-service-quantity
        :caseId="item.caseId"
        :service-id="item.serviceId"
        :executed-quantity="item.executedQuantity"
        :editable="false"
        @executedQuantityChanged="handleExecutedQuantityChanged"/>
    </td>
    <td>
      <v-text-field
        ref="comment"
        v-model="comment"
        :rules="commentRules"
        counter="1000"
        maxlength="1000"
      />
    </td>
    <td class="text-right">
      <v-btn flat icon color="green lighten-2" class="" @click="handleSaveButtonClick">
        <v-icon>fas fa-check</v-icon>
      </v-btn>
      <v-btn flat icon color="red lighten-2" class="ma-0 pa-0" @click="handleEditButtonClick">
        <v-icon>fas fa-times</v-icon>
      </v-btn>
    </td>
  </tr>
  <tr v-else>
    <td>
      <v-checkbox v-model="bindToCase"></v-checkbox>
    </td>
    <td>
      <div class="mb-2">
        <version-tooltip :version=item.clientServiceVersion>
          {{ item.title }}
        </version-tooltip>
      </div>
    </td>
    <td>{{ item.code }}</td>
    <td><span v-bind:class="{ priceOverridden: item.priceOverridden }">{{ price | currency}}</span><confirm ref="confirm"></confirm></td>
    <td>
      <requested-service-quantity
        :caseId="item.caseId"
        :service-id="item.serviceId"
        :requested-quantity="item.quantity"
        :editable="true"
        @requestedQuantityChanged="handleRequestedQuantityChanged"/>
    </td>
    <td>
      <executed-service-quantity
        :caseId="item.caseId"
        :service-id="item.serviceId"
        :executed-quantity="item.executedQuantity"
        :editable="true"
        @executedQuantityChanged="handleExecutedQuantityChanged"/>
    </td>
    <td>{{ item.comment | NoEmpty }}</td>
    <td class="justify-center layout px-0">
      <v-icon small class="mr-2" @click="handleEditButtonClick" v-if="item.addedToCase">edit</v-icon>
    </td>
  </tr>
</template>

<script>
import _ from 'lodash'
import Confirm from '../Ui/Confirm'
import NoEmpty from '../Filters/NoEmpty'
import {showApiError} from '../../helpers/notification.js'
import api from '../../api'
import {isNumeric} from '../../helpers/types'
import VersionTooltip from '../Ui/VersionTooltip'
import ExecutedServiceQuantity from './case/ExecutedServiceQuantity'
import RequestedServiceQuantity from './case/RequestedServiceQuantity'

export default {
  name: 'case-service-item',
  props: {
    item: {
      type: Object
    }
  },
  components: {
    Confirm,
    VersionTooltip,
    ExecutedServiceQuantity,
    RequestedServiceQuantity
  },
  filters: {
    NoEmpty
  },
  data () {
    return {
      isEditMode: false,
      quantityOptions: _.range(1, 51),
      validatableRefs: ['price', 'comment'],
      draft: {
        id: this.item.id,
        price: this.item.price || this.item.defaultPrice,
        currency: this.item.currency || this.item.defaultCurrency,
        quantity: this.item.quantity,
        executedQuantity: this.item.executedQuantity,
        comment: this.item.comment,
        addedToCase: this.item.addedToCase
      },
      commentRules: [
        (v) => {
          if (!v) {
            return true
          }

          if (v.length > 1000) {
            return 'Comment must be no more than 1000 characters'
          }

          return true
        }
      ],
      priceRules: [
        (value) => {
          let numeric = isNumeric(value)

          if (!numeric) {
            return 'Price must be numeric value'
          }

          if (parseFloat(value) < 0) {
            return 'Price value must be positive or zero'
          }

          return true
        }
      ]
    }
  },
  computed: {
    priceAdditionalClasses: {
      get: function () {
        if (this.item.priceOverridden) {
          return 'fas fa-times priceOverridden ma-0 pa-0'
        }

        return null
      }
    },

    bindToCase: {
      get: function () {
        return this.item.addedToCase
      },
      set: async function (newValue) {
        if (newValue === false) {
          this.isEditMode = false
          if (this.item.comment != null || this.item.quantity > 1 || this.item.priceOverridden) {
            this.$refs.confirm.open('Detach service from case', 'Are you sure?', {width: 350}).then(async (confirm) => {
              if (!confirm) {
                return
              }
              this.removeServiceFromCase(this.item.id)
            })
          } else {
            this.removeServiceFromCase(this.item.id)
          }
        } else {
          this.addServiceToCase(this.item.caseId, this.item.serviceId)
        }
      }
    },
    price: {
      get: function () {
        return this.item.price || this.item.defaultPrice
      },
      set: function (newValue) {
        this.draft.price = newValue
      }
    },
    quantity: {
      get: function () {
        return this.item.quantity
      },
      set: function (newValue) {
        this.draft.quantity = newValue
      }
    },
    comment: {
      get: function () {
        return this.item.comment
      },
      set: function (newValue) {
        this.draft.comment = newValue
      }
    }
  },
  methods: {
    updateDraft (item) {
      this.draft = {
        id: item.id,
        price: item.price || this.item.defaultPrice,
        currency: item.currency || this.item.defaultCurrency,
        quantity: item.quantity,
        comment: item.comment,
        addedToCase: !!item.id
      }
    },
    async handleResetPriceButtonClick () {
      this.$emit('startProcessing')

      try {
        let updatedItem = await api.resetServicePrice(this.item.id)
        this.draft.price = this.item.defaultPrice
        this.draft.currency = this.item.defaultCurrency
        this.$emit('updatedCaseService', updatedItem)
        this.$emit('stopProcessing')
        this.updateCasePrice(this.item.caseId)
      } catch (e) {
        showApiError(this, e, 'Unable to get case price')
        this.$emit('stopProcessing')
      }
    },
    async updateCasePrice (caseId) {
      this.$emit('startProcessing')

      try {
        let result = await api.getCasePrice(caseId)
        this.$emit('casePriceUpdated', {
          caseId: caseId,
          price: result.price
        })
        this.$emit('stopProcessing')
      } catch (e) {
        showApiError(this, e, 'Unable to get case price')
        this.$emit('stopProcessing')
      }
    },
    async handleSaveButtonClick () {
      for (let refName of this.validatableRefs) {
        if (!this.$refs[refName].validate(true)) {
          return false
        }
      }

      try {
        this.$emit('startProcessing')

        let comment = this.draft.comment

        if (comment && comment.length === 0) {
          comment = null
        }

        let updatedItem = await api.updateCaseService(this.item.id, parseFloat(this.draft.price), this.draft.currency, this.draft.quantity, comment)

        this.isEditMode = false
        this.$emit('updatedCaseService', updatedItem)
        this.$emit('stopProcessing')
        this.updateCasePrice(this.item.caseId)
        this.updateDraft(updatedItem)
      } catch (e) {
        showApiError(this, e, 'Unable to update case service')
        this.$emit('stopProcessing')
      }
    },
    handleEditButtonClick () {
      this.isEditMode = !this.isEditMode
    },
    handleCaseBindingChange (value) {
      console.log(value)
    },
    async removeServiceFromCase (id) {
      this.$emit('startProcessing')

      try {
        await api.removeServiceFromCase(id)
        this.$emit('removedServiceFromCase', {
          caseId: this.item.caseId,
          serviceId: this.item.serviceId,
          caseServiceId: id
        })
        this.$emit('stopProcessing')
        this.updateCasePrice(this.item.caseId)
      } catch (e) {
        showApiError(this, e, 'Unable to remove service from case')
        this.$emit('stopProcessing')
      }
    },
    async addServiceToCase (caseId, serviceId) {
      this.$emit('startProcessing')

      try {
        let result = await api.addServiceToCase(caseId, serviceId)
        this.$emit('addedServiceToCase', result)
        this.$emit('stopProcessing')
        this.updateCasePrice(caseId)
        this.updateDraft(result)
      } catch (e) {
        showApiError(this, e, 'Unable to add service to case')
        this.$emit('stopProcessing')
      }
    },
    handleRequestedQuantityChanged(event) {
      this.$emit('requestedQuantityChanged', {
        ...event,
        caseId: this.item.caseId,
        serviceId: this.item.serviceId
      })
    },
    handleExecutedQuantityChanged(event) {
      this.$emit('executedQuantityChanged', {
        ...event,
        caseId: this.item.caseId,
        serviceId: this.item.serviceId
      })
    },
  }
}
</script>

<style>
  .priceOverridden {
    color: #e57373 !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/Sales/CaseServiceItem.vue